/**
 * Действия в диалоге: отправка, повтор, редактирование, удаление, реакции,
 * квитанции, mute/архив.
 *
 * CC §5.4 «Алгоритм отправки»: сообщение СНАЧАЛА появляется в истории как
 * ожидающее, и только потом уходит в сеть. `client_message_id` создаётся
 * один раз и не меняется при повторах (§5.2) — на нём держится идемпотентность.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { api, unwrap, uuid } from '@shared/api/client'
import type { Conversation, Message, PollCreate } from '@shared/api/types'
import type { SecretMediaMeta, SecretPayload } from '@shared/crypto/payload'
import {
  conversationsRepo,
  currentUser,
  draftsRepo,
  historyLatestRepo,
  messagesRepo,
  queueRepo,
  tryUserDb,
  type LocalMediaDraft,
  type LocalSecretMeta,
  type PendingOp,
  type SendMessageOp,
} from '@shared/db'
import { currentTtl, decryptIncoming, encryptOutgoing, noteSecretArmed, rememberTtl } from '@shared/secret'
import { triggerQueue } from '@shared/sync/queue-runner'

/** Тип вложения секретного чата по черновику композера. */
function secretMediaType(media: LocalMediaDraft): SecretMediaMeta['type'] {
  const type = media.secret?.content_type ?? media.content_type ?? ''
  if (media.purpose === 'chat_file') return 'file'
  if (type.startsWith('video')) return 'video'
  if (type.startsWith('audio')) return 'voice'
  if (type.startsWith('image')) return 'image'
  return 'file'
}

/**
 * Страница истории секретного чата: сообщения, которых ещё нет в реплике, вскрываются
 * по порядку `seq` (пропуски рэтчет переживает), а уже известные проходят как есть —
 * их ключи израсходованы, текст сохранит `secretCarryOver` репозитория.
 */
async function prepareSecretHistory(
  conversation: Conversation,
  items: readonly Message[],
  myUserId: string,
): Promise<Message[]> {
  if (conversation.kind !== 'secret') return [...items]
  const ordered = [...items].sort((a, b) => a.seq - b.seq)
  const prepared: Message[] = []
  for (const item of ordered) {
    if (item.type !== 'encrypted' || item.sender_id === myUserId || !item.envelope) {
      prepared.push(item)
      continue
    }
    if (await messagesRepo.get(item.conversation_id, item.seq)) {
      prepared.push(item)
      continue
    }
    prepared.push(await decryptIncoming(item, myUserId))
  }
  return prepared
}

interface SendInput {
  conversationId: string
  senderId: string
  topic?: NonNullable<Message['topic']>
  text: string
  replyTo?: Message['reply_to']
  /** Комментарий под постом канала; отправляется общей durable-очередью сообщений. */
  commentOf?: string
  media?: LocalMediaDraft | null
  mediaIds?: string[]
  draftVersion?: number
  entities?: Message['entities']
  draftKey?: string
  /** Подпись пересылки будет поставлена в очередь следом и завершит отправку черновика. */
  preserveDraft?: boolean
  /** Пересылка: id исходного сообщения; тип и текст копируются для локального показа. */
  forwardOf?: string
  forwardType?: 'text' | 'image' | 'video' | 'voice' | 'file'
}

function baseOp(kind: PendingOp['kind']): Omit<PendingOp, 'kind'> & { kind: PendingOp['kind'] } {
  return {
    op_id: uuid(),
    kind,
    created_at: new Date().toISOString(),
    attempt_count: 0,
    next_attempt_at: null,
    last_error: null,
    claimed_by: null,
    claimed_at: null,
    idempotency_key: null,
  } as never
}

interface ActiveHistoryRequest {
  controller: AbortController
  discarded: boolean
}

interface HistoryRequestScope {
  epoch: number
  exhaustedAt: number | null | undefined
  requests: Set<ActiveHistoryRequest>
}

// Desktop держит список и открытый чат в разных экземплярах hook. Генерация
// запросов поэтому общая для диалога внутри конкретной пользовательской БД.
const historyRequestScopes = new Map<string, HistoryRequestScope>()

function historyRequestKey(conversationId: string): string {
  return `${tryUserDb()?.name ?? 'closed'}\u0000${conversationId}`
}

function historyRequestScope(key: string): HistoryRequestScope {
  const existing = historyRequestScopes.get(key)
  if (existing) return existing
  const created: HistoryRequestScope = { epoch: 0, exhaustedAt: undefined, requests: new Set() }
  historyRequestScopes.set(key, created)
  return created
}

export function useChatActions() {
  const ownedHistoryRequests = useRef(new Set<ActiveHistoryRequest>())

  const invalidateHistoryRequests = useCallback((conversationId: string): void => {
    const scope = historyRequestScope(historyRequestKey(conversationId))
    scope.epoch += 1
    scope.exhaustedAt = undefined
    for (const request of scope.requests) {
      request.discarded = true
      request.controller.abort()
    }
  }, [])

  const startHistoryRequest = useCallback((conversationId: string, upstream?: AbortSignal) => {
    const key = historyRequestKey(conversationId)
    const scope = historyRequestScope(key)
    const epoch = scope.epoch
    const controller = new AbortController()
    const active: ActiveHistoryRequest = { controller, discarded: false }
    scope.requests.add(active)
    ownedHistoryRequests.current.add(active)

    const onUpstreamAbort = (): void => controller.abort()
    if (upstream?.aborted) controller.abort()
    else upstream?.addEventListener('abort', onUpstreamAbort, { once: true })

    return {
      signal: controller.signal,
      stale: (): boolean => active.discarded || historyRequestScopes.get(key)?.epoch !== epoch,
      exhaustedAt: (): number | null | undefined => scope.exhaustedAt,
      setExhaustedAt: (oldest: number | null): void => {
        scope.exhaustedAt = oldest
      },
      finish: (): void => {
        upstream?.removeEventListener('abort', onUpstreamAbort)
        scope.requests.delete(active)
        ownedHistoryRequests.current.delete(active)
      },
    }
  }, [])

  // После logout/смены аккаунта поздний HTTP-ответ не должен писать
  // в уже открытую базу другого пользователя.
  useEffect(
    () => () => {
      for (const request of ownedHistoryRequests.current) {
        request.discarded = true
        request.controller.abort()
      }
    },
    [],
  )
  /* --- Отправка ------------------------------------------------------- */

  const send = useCallback(async (input: SendInput): Promise<void> => {
    const draftKey = input.draftKey ?? input.conversationId
    const savedDraft = await draftsRepo.get(draftKey)
    const text = input.text.trim()
    // У пересылки своего тела нет: пустая подпись — нормальный случай.
    if (!text && !input.media && !input.forwardOf) return

    // §5.2 п. 1: один `client_message_id` на сообщение, создаётся здесь.
    const clientMessageId = uuid()

    // Секретный чат (изменение v1.13): текст и ключ вложения уходят только внутри конверта.
    const conversation = await conversationsRepo.get(input.conversationId)
    if (conversation?.kind === 'secret') {
      if (input.forwardOf) throw new Error('secret_forward_unsupported')
      const ttl = await currentTtl(input.conversationId)
      const replyTo = input.replyTo?.id ? { id: input.replyTo.id } : null
      const media = input.media
      const payload: SecretPayload =
        media?.secret && media.media_id
          ? {
              v: 1,
              kind: 'media',
              ttl,
              text: text || null,
              reply_to: replyTo,
              media: {
                type: secretMediaType(media),
                key: media.secret.key,
                iv: media.secret.iv,
                digest: media.secret.digest,
                size: media.secret.size_bytes,
                content_type: media.secret.content_type,
                file_name: media.file_name ?? null,
                width: media.width ?? null,
                height: media.height ?? null,
                duration_ms: media.duration_ms ?? null,
                thumb: media.secret.thumb ?? null,
                thumb_type: media.secret.thumb_type ?? null,
              },
            }
          : { v: 1, kind: 'text', text, ttl, reply_to: replyTo }
      const envelope = await encryptOutgoing(input.conversationId, payload)
      const secretMeta: LocalSecretMeta = {
        kind: payload.kind === 'media' ? 'media' : 'text',
        ttl,
        media: payload.kind === 'media' ? payload.media : null,
        reply_to_id: replyTo?.id ?? null,
      }
      await messagesRepo.enqueueLocal(
        {
          conversationId: input.conversationId,
          clientMessageId,
          senderId: input.senderId,
          type: 'encrypted',
          text: text || null,
          replyTo: input.replyTo ?? null,
          media: media ?? null,
          secret: secretMeta,
          envelope,
        },
        {
          ...baseOp('send_message'),
          kind: 'send_message',
          conversation_id: input.conversationId,
          client_message_id: clientMessageId,
          envelope,
          idempotency_key: uuid(),
        } as SendMessageOp,
      )
      if (savedDraft)
        await draftsRepo.clearIfUnchanged(draftKey, savedDraft.updated_at, savedDraft.local_version)
      triggerQueue()
      return
    }

    const operation = {
      ...baseOp('send_message'),
      kind: 'send_message',
      conversation_id: input.conversationId,
      ...(input.topic ? { topic_id: input.topic.id } : {}),
      client_message_id: clientMessageId,
      ...(input.mediaIds && input.mediaIds.length > 1 ? { media_ids: input.mediaIds } : {}),
      ...(input.draftVersion ? { draft_version: input.draftVersion } : {}),
      ...(input.commentOf ? { comment_of: input.commentOf } : {}),
      ...(input.forwardOf ? { forward_of: input.forwardOf } : {}),
      // §10.4: `Idempotency-Key` — один на намерение, переживает повторы.
      idempotency_key: uuid(),
    } as SendMessageOp

    await messagesRepo.enqueueLocal(
      {
        conversationId: input.conversationId,
        clientMessageId,
        senderId: input.senderId,
        type:
          input.forwardType ??
          (input.media
            ? input.media.purpose === 'chat_file'
              ? 'file'
              : input.media.content_type?.startsWith('video')
                ? 'video'
                : input.media.content_type?.startsWith('audio')
                  ? 'voice'
                  : 'image'
            : 'text'),
        text: text || null,
        topic: input.topic ?? null,
        entities: input.forwardOf ? [] : input.entities,
        replyTo: input.replyTo ?? null,
        commentOf: input.commentOf ?? null,
        media: input.media ?? null,
        forwarded: Boolean(input.forwardOf),
        forwardOf: input.forwardOf ?? null,
      },
      operation,
    )

    // Поле комментария отдельное от композера ленты канала: его отправка не должна
    // уничтожать незавершённый черновик нового поста.
    if (!input.commentOf && !input.preserveDraft && savedDraft)
      await draftsRepo.clearIfUnchanged(draftKey, savedDraft.updated_at, savedDraft.local_version)
    triggerQueue()
  }, [])

  const sendPoll = useCallback(
    async (input: {
      conversationId: string
      senderId: string
      poll: PollCreate
      topic?: NonNullable<Message['topic']>
    }): Promise<void> => {
      const clientMessageId = uuid()
      const operation: SendMessageOp = {
        ...baseOp('send_message'),
        kind: 'send_message',
        conversation_id: input.conversationId,
        ...(input.topic ? { topic_id: input.topic.id } : {}),
        client_message_id: clientMessageId,
        poll: input.poll,
        idempotency_key: uuid(),
      }
      await messagesRepo.enqueueLocal(
        {
          conversationId: input.conversationId,
          clientMessageId,
          senderId: input.senderId,
          type: 'poll',
          text: input.poll.question,
          poll: {
            question: input.poll.question,
            options: input.poll.options.map((option) => ({ id: uuid(), text: option, votes: 0 })),
            multiple: input.poll.multiple,
            anonymous: input.poll.anonymous,
            closes_at: input.poll.closes_at ?? null,
            closed_at: null,
            closed: false,
            voted_option_ids: [],
            total_voters: 0,
          },
          pollCreate: input.poll,
          topic: input.topic ?? null,
        },
        operation,
      )
      triggerQueue()
    },
    [],
  )

  /** C-19: обычный retry сохраняет id; immutable-ID conflict создаёт новое намерение. */
  const retry = useCallback(async (conversationId: string, clientMessageId: string): Promise<void> => {
    const local = await messagesRepo.byClientMessageId(clientMessageId)
    if (local?.error_code === 'client_message_id_conflict' && local.sender_id && local.type !== 'system') {
      const oldOp = (await queueRepo.byConversation(conversationId)).find(
        (op): op is SendMessageOp => op.kind === 'send_message' && op.client_message_id === clientMessageId,
      )
      const albumMediaIds = local.local_album_media_ids ?? oldOp?.media_ids
      const nextClientMessageId = uuid()
      const operation = {
        ...baseOp('send_message'),
        kind: 'send_message',
        conversation_id: conversationId,
        client_message_id: nextClientMessageId,
        ...(albumMediaIds?.length ? { media_ids: [...albumMediaIds] } : {}),
        ...(local.comment_of ? { comment_of: local.comment_of } : {}),
        ...(local.local_forward_of ? { forward_of: local.local_forward_of } : {}),
        ...(local.local_poll_create ? { poll: local.local_poll_create } : {}),
        ...(local.topic ? { topic_id: local.topic.id } : {}),
        idempotency_key: uuid(),
      } as SendMessageOp
      await messagesRepo.enqueueLocal(
        {
          conversationId,
          clientMessageId: nextClientMessageId,
          senderId: local.sender_id,
          type: local.type,
          poll: local.poll,
          pollCreate: local.local_poll_create,
          topic: local.topic,
          text: local.text,
          replyTo: local.reply_to,
          commentOf: local.comment_of,
          media: local.local_media,
          forwarded: local.forwarded,
          forwardOf: local.local_forward_of,
        },
        operation,
      )
      // Новая durable-запись уже создана: теперь старый конфликт
      // можно убрать без риска потерять текст/медиа.
      await messagesRepo.dropUnconfirmed(clientMessageId)
      triggerQueue()
      return
    }
    await queueRepo.retrySend({
      ...baseOp('send_message'),
      kind: 'send_message',
      conversation_id: conversationId,
      client_message_id: clientMessageId,
      ...(local?.local_envelope ? { envelope: local.local_envelope } : {}),
      ...(local?.local_album_media_ids?.length ? { media_ids: [...local.local_album_media_ids] } : {}),
      ...(local?.comment_of ? { comment_of: local.comment_of } : {}),
      ...(local?.local_forward_of ? { forward_of: local.local_forward_of } : {}),
      ...(local?.local_poll_create ? { poll: local.local_poll_create } : {}),
      ...(local?.topic ? { topic_id: local.topic.id } : {}),
      idempotency_key: uuid(),
    } as SendMessageOp)

    triggerQueue()
  }, [])

  /** C-19 «Удалить черновик»: запись исчезает из истории. */
  const discard = useCallback(async (conversationId: string, clientMessageId: string): Promise<void> => {
    await messagesRepo.dropUnconfirmed(clientMessageId)

    const ops = await queueRepo.byConversation(conversationId)
    for (const op of ops) {
      if (op.kind === 'send_message' && op.client_message_id === clientMessageId) {
        await queueRepo.remove(op.op_id)
      }
    }
    triggerQueue()
  }, [])

  /* --- Редактирование -------------------------------------------------- */

  const edit = useCallback(
    async (
      conversationId: string,
      message: { id: string; seq: number },
      text: string,
      draftVersion?: number,
      entities?: Message['entities'],
    ): Promise<void> => {
      const draftKey = `${conversationId}::edit`
      const savedDraft = await draftsRepo.get(draftKey)
      // Оптимистично правим локально: пользователь видит результат сразу.
      const local = await messagesRepo.get(conversationId, message.seq)
      if (!local || local.id !== message.id || local.version < 1) {
        throw new Error('message is no longer available for a safe edit')
      }
      const previousText = local.text ?? ''
      await messagesRepo.setLocalState(conversationId, message.seq, { text, entities: entities ?? [] })

      await queueRepo.enqueue({
        ...baseOp('edit_message'),
        kind: 'edit_message',
        ...(draftVersion ? { draft_version: draftVersion } : {}),
        conversation_id: conversationId,
        message_id: message.id,
        message_seq: message.seq,
        base_version: local.version,
        previous_text: previousText,
        previous_entities: local.entities ?? [],
        entities: entities ?? [],
        text,
      } as PendingOp)

      if (savedDraft)
        await draftsRepo.clearIfUnchanged(draftKey, savedDraft.updated_at, savedDraft.local_version)
      triggerQueue()
    },
    [],
  )

  /* --- Удаление --------------------------------------------------------- */

  /**
   * Таймер самоуничтожения секретного чата (v1.13): новое значение уходит собеседнику
   * служебным сообщением внутри конверта и применяется к следующим отправкам.
   */
  const setSelfDestruct = useCallback(
    async (conversationId: string, senderId: string, ttl: number): Promise<void> => {
      const envelope = await encryptOutgoing(conversationId, { v: 1, kind: 'timer', ttl })
      await rememberTtl(conversationId, ttl)
      const clientMessageId = uuid()
      await messagesRepo.enqueueLocal(
        {
          conversationId,
          clientMessageId,
          senderId,
          type: 'encrypted',
          text: null,
          secret: { kind: 'timer', ttl, media: null, reply_to_id: null },
          envelope,
        },
        {
          ...baseOp('send_message'),
          kind: 'send_message',
          conversation_id: conversationId,
          client_message_id: clientMessageId,
          envelope,
          idempotency_key: uuid(),
        } as SendMessageOp,
      )
      triggerQueue()
    },
    [],
  )

  const remove = useCallback(
    async (conversationId: string, messageId: string, scope: 'me' | 'all'): Promise<void> => {
      await queueRepo.enqueue({
        ...baseOp('delete_message'),
        kind: 'delete_message',
        conversation_id: conversationId,
        message_id: messageId,
        scope,
      } as PendingOp)

      triggerQueue()
    },
    [],
  )

  /* --- Реакции ----------------------------------------------------------- */

  const react = useCallback(
    async (
      conversationId: string,
      message: { id: string; seq: number },
      reaction: string | null,
      myUserId: string,
    ): Promise<void> => {
      // Оптимистичное применение: одна реакция на пользователя (BS §4.2).
      const local = await messagesRepo.get(conversationId, message.seq)
      if (local) {
        await messagesRepo.setOwnReaction(conversationId, message.seq, myUserId, reaction)
      }

      await queueRepo.enqueue({
        ...baseOp(reaction ? 'set_reaction' : 'remove_reaction'),
        kind: reaction ? 'set_reaction' : 'remove_reaction',
        conversation_id: conversationId,
        message_id: message.id,
        ...(reaction ? { reaction } : {}),
      } as PendingOp)

      triggerQueue()
    },
    [],
  )

  /* --- Квитанции ---------------------------------------------------------- */

  /**
   * CC §5.8: курсоры монотонны. Durable HTTP-op — единственный источник
   * отправки, чтобы WS-ускорение не обходило batch и лимит один раз / 2 с.
   */
  const markRead = useCallback(
    async (conversationId: string, seq: number, forceReceipt = false): Promise<void> => {
      const conversation = await conversationsRepo.get(conversationId)
      if (!conversation || (!forceReceipt && conversation.my.last_read_seq >= seq)) return

      if (conversation.my.last_read_seq < seq)
        await conversationsRepo.patchOptimistic(conversationId, {
          my: {
            ...conversation.my,
            last_read_seq: seq,
            last_delivered_seq: Math.max(conversation.my.last_delivered_seq, seq),
          },
          unread_count: 0,
        })

      await queueRepo.mergeReceipt(conversationId, seq, seq)
      // Секретный чат: прочтение взводит таймер входящих сразу, не дожидаясь квитанции (v1.13).
      if (conversation.kind === 'secret') {
        const myUserId = currentUser()
        if (
          myUserId &&
          (await messagesRepo.armSelfDestruct(conversationId, seq, { myUserId, mine: false })) > 0
        )
          noteSecretArmed()
      }
      triggerQueue()
    },
    [],
  )

  /* --- Проекция диалога ----------------------------------------------------- */

  const setMuted = useCallback(async (conversationId: string, mutedUntil: string | null): Promise<void> => {
    await conversationsRepo.patchOptimistic(conversationId, {
      my: { ...(await conversationsRepo.get(conversationId))!.my, muted_until: mutedUntil },
    })

    await queueRepo.enqueue({
      ...baseOp('patch_conversation'),
      kind: 'patch_conversation',
      conversation_id: conversationId,
      patch: { muted_until: mutedUntil },
    } as PendingOp)

    triggerQueue()
  }, [])

  const setArchived = useCallback(async (conversationId: string, archived: boolean): Promise<void> => {
    const conversation = await conversationsRepo.get(conversationId)
    if (!conversation) return

    await conversationsRepo.patchOptimistic(conversationId, {
      my: { ...conversation.my, archived_at: archived ? new Date().toISOString() : null },
    })

    await queueRepo.enqueue({
      ...baseOp('patch_conversation'),
      kind: 'patch_conversation',
      conversation_id: conversationId,
      patch: { archived },
    } as PendingOp)

    triggerQueue()
  }, [])

  const setPersonal = useCallback(
    async (
      conversationId: string,
      patch: { pinned_order?: number | null; manual_unread?: boolean },
    ): Promise<void> => {
      const conversation = await conversationsRepo.get(conversationId)
      if (!conversation) return
      await conversationsRepo.patchOptimistic(conversationId, { my: { ...conversation.my, ...patch } })
      await queueRepo.enqueue({
        ...baseOp('patch_conversation'),
        kind: 'patch_conversation',
        conversation_id: conversationId,
        patch,
      } as PendingOp)
      triggerQueue()
    },
    [],
  )

  /**
   * CC §4.9 п. 10: очистка истории и скрытие диалога выполняются ТОЛЬКО онлайн.
   * Граница очистки вычисляется сервером в момент выполнения, поэтому
   * отложенный повтор необратимо удалил бы пришедшие за время офлайна сообщения.
   */
  /** Закрепить сообщение (`messageId`) или снять закреплённое (`null`) — факт общий для пары. */
  const pin = useCallback(async (conversationId: string, messageId: string | null): Promise<void> => {
    await queueRepo.enqueue({
      ...baseOp('pin_message'),
      kind: 'pin_message',
      conversation_id: conversationId,
      message_id: messageId,
    } as PendingOp)
    triggerQueue()
  }, [])

  const clearHistory = useCallback(
    async (conversationId: string): Promise<void> => {
      // Запрос старой страницы может завершиться уже после clear. Отменяем его
      // и меняем epoch до HTTP-вызова, чтобы даже игнорирующий abort транспорт
      // не восстановил только что удалённую историю.
      invalidateHistoryRequests(conversationId)
      // Локальная граница соответствует моменту нажатия. Новое WS-сообщение,
      // пришедшее пока POST в пути, уже не входит в эту очистку.
      const conversation = await conversationsRepo.get(conversationId)
      await api.POST('/conversations/{conversationId}/clear', {
        params: { path: { conversationId } },
      })

      if (!conversation) return
      await messagesRepo.clearBefore(conversationId, conversation.last_seq + 1)
      await historyLatestRepo.advance(conversationId, {
        through_seq: conversation.last_seq,
        max_feed_seq: null,
      })
    },
    [invalidateHistoryRequests],
  )

  const hideConversation = useCallback(
    async (conversationId: string): Promise<void> => {
      invalidateHistoryRequests(conversationId)
      // Boundary фиксируется до DELETE: confirmed сообщения после неё и
      // synthetic pending (> PENDING_SEQ_BASE) принадлежат уже новой эпохе.
      const conversation = await conversationsRepo.get(conversationId)
      await api.DELETE('/conversations/{conversationId}', {
        params: { path: { conversationId } },
      })

      if (!conversation) return
      await messagesRepo.clearBefore(conversationId, conversation.last_seq + 1)
      await conversationsRepo.deleteIfNotNewer(conversationId, conversation.version, conversation.last_seq)
    },
    [invalidateHistoryRequests],
  )

  /* --- Подгрузка истории ------------------------------------------------------ */

  /**
   * Первичная загрузка истории при открытии диалога.
   *
   * Реплика наполняется списками диалогов, но НЕ сообщениями (CC §4.3 шаг 5):
   * история грузится по требованию. Загружаем последнюю страницу, если
   * локально нет сообщений вплоть до `last_seq` — иначе открытый диалог
   * выглядел бы пустым при непустом превью в списке.
   */
  const ensureHistory = useCallback(
    async (conversationId: string, myUserId: string): Promise<void> => {
      const request = startHistoryRequest(conversationId)
      try {
        const conversation = await conversationsRepo.get(conversationId)
        if (!conversation || request.stale()) return
        request.signal.throwIfAborted()

        const [localMax, latestPage] = await Promise.all([
          messagesRepo.maxConfirmedSeq(conversationId),
          historyLatestRepo.get(conversationId),
        ])
        if (request.stale()) return
        request.signal.throwIfAborted()
        if (localMax >= conversation.last_seq) return
        if (
          latestPage &&
          latestPage.through_seq >= conversation.last_seq &&
          (latestPage.max_feed_seq === null || localMax >= latestPage.max_feed_seq)
        ) {
          return
        }

        let result
        try {
          result = unwrap(
            await api.GET('/conversations/{conversationId}/messages', {
              params: { path: { conversationId }, query: { limit: 50 } as never },
              signal: request.signal,
            }),
          )
        } catch (error) {
          // clear/hide/unmount — ожидаемая отмена. Сетевую ошибку актуального
          // запроса пробрасываем как раньше.
          if (request.stale()) return
          throw error
        }

        // Главный fence стоит после HTTP и до первой записи в Dexie.
        if (request.stale()) return
        request.signal.throwIfAborted()
        const latestItems = await prepareSecretHistory(conversation, result.items, myUserId)
        if (request.stale()) return
        await messagesRepo.putManyConfirmed(latestItems, myUserId, conversation.counterpart_receipts)
        if (request.stale()) return
        const maxFeedSeq = result.items.reduce<number | null>(
          (maximum, message) => (message.comment_of ? maximum : Math.max(maximum ?? 0, message.seq)),
          null,
        )
        await historyLatestRepo.advance(conversationId, {
          through_seq: conversation.last_seq,
          max_feed_seq: maxFeedSeq,
        })
      } finally {
        request.finish()
      }
    },
    [startHistoryRequest],
  )

  const loadOlder = useCallback(
    async (conversationId: string, myUserId: string, signal?: AbortSignal): Promise<boolean> => {
      const request = startHistoryRequest(conversationId, signal)
      try {
        const oldest = await messagesRepo.minConfirmedSeq(conversationId)
        if (request.stale()) return false
        request.signal.throwIfAborted()
        if (oldest !== null && oldest <= 1) return false
        if (request.exhaustedAt() !== undefined && request.exhaustedAt() === oldest) return false

        const conversation = await conversationsRepo.get(conversationId)
        if (request.stale()) return false
        request.signal.throwIfAborted()

        let result
        try {
          result = unwrap(
            await api.GET('/conversations/{conversationId}/messages', {
              params: {
                path: { conversationId },
                query: { limit: 50, ...(oldest ? { before: oldest } : {}) },
              },
              signal: request.signal,
            }),
          )
        } catch (error) {
          if (request.stale()) return false
          throw error
        }

        if (request.stale()) return false
        request.signal.throwIfAborted()
        const olderItems = conversation
          ? await prepareSecretHistory(conversation, result.items, myUserId)
          : [...result.items]
        if (request.stale()) return false
        await messagesRepo.putManyConfirmed(olderItems, myUserId, conversation?.counterpart_receipts)
        if (request.stale()) return false
        if (!result.has_more) {
          const minimum = await messagesRepo.minConfirmedSeq(conversationId)
          if (request.stale()) return false
          request.setExhaustedAt(minimum)
        }
        return result.items.length > 0
      } finally {
        request.finish()
      }
    },
    [startHistoryRequest],
  )

  /** Догружаем непрерывную историю до найденного сообщения, сохраняя обычный курсор ленты. */
  const loadThrough = useCallback(
    async (conversationId: string, myUserId: string, seq: number, signal: AbortSignal): Promise<boolean> => {
      for (;;) {
        signal.throwIfAborted()
        if (await messagesRepo.get(conversationId, seq)) return true
        const oldest = await messagesRepo.minConfirmedSeq(conversationId)
        if (oldest !== null && oldest <= seq) return false
        const more = await loadOlder(conversationId, myUserId, signal)
        if (await messagesRepo.get(conversationId, seq)) return true
        const next = await messagesRepo.minConfirmedSeq(conversationId)
        // Очищенная история, удалённая цель или сервер без прогресса не зацикливают переход.
        if (!more || next === null || (oldest !== null && next >= oldest)) return false
      }
    },
    [loadOlder],
  )

  /*
   * Объект действий обязан быть стабильным: экраны держат его в зависимостях
   * эффектов. Без `useMemo` каждая отрисовка давала новый объект, эффект
   * первичной загрузки истории перезапускался бесконечно и сбрасывал признак
   * «история загружена» — пустой диалог так и оставался без «Начните разговор».
   */
  return useMemo(
    () => ({
      send,
      sendPoll,
      retry,
      discard,
      ensureHistory,
      edit,
      remove,
      react,
      markRead,
      setMuted,
      setArchived,
      setPersonal,
      pin,
      clearHistory,
      hideConversation,
      loadOlder,
      loadThrough,
      setSelfDestruct,
    }),
    [
      send,
      sendPoll,
      retry,
      discard,
      ensureHistory,
      edit,
      remove,
      react,
      markRead,
      setSelfDestruct,
      setMuted,
      setArchived,
      setPersonal,
      pin,
      clearHistory,
      hideConversation,
      loadOlder,
      loadThrough,
    ],
  )
}
